import type { IDenialPaymentRow } from './denial-payments';
import type { IDenialRemarkRow } from './denial-remark-rows';
import type { IRejectionRow } from './rejection-rows';

/**
 * The search box on the Denials & Rejections page (FE-116 feedback, 2026-10-06 — it was in the earlier design
 * and v3 dropped it).
 *
 * A row matches when EVERY word typed is found in one of its fields, case-insensitively. A word is also tried
 * with punctuation removed on both sides, so `co45`, `CO-45` and `co:45` all find the CO-45 rows.
 *
 * What each view can be searched by is what its rows carry. A denials row is a code + remark combination
 * summed over the week, so it has no patient and no claim ID to search; the payments behind it do not carry
 * a patient either. Those words are not offered in the placeholder, because nothing would match them.
 */

const packed = (text: string): string => text.toLowerCase().replace(/[^a-z0-9]/g, '');

/** The words of a query, lower-cased. Empty when there is nothing to search for. */
export const searchTerms = (query: string): string[] => query.toLowerCase().split(/\s+/).filter(Boolean);

const matchesFields = (fields: Array<string | undefined>, terms: string[]): boolean => {
    if (!terms.length) {
        return true;
    }
    const present = fields.filter((field): field is string => Boolean(field));
    const plain = present.map(field => field.toLowerCase());
    const compact = present.map(packed);

    return terms.every(term => {
        const bare = packed(term);
        return (
            plain.some(field => field.includes(term)) ||
            (bare.length > 0 && compact.some(field => field.includes(bare)))
        );
    });
};

/** `CO-45`, its remark codes, and the reason and remark descriptions. */
export const denialRowMatches = (row: IDenialRemarkRow, terms: string[]): boolean =>
    matchesFields(
        [row.codes.replace(':', '-'), ...row.remarkCodes, row.reasonDescription, row.remarkDescription],
        terms,
    );

/** The message as sent, its status code (`Code 21`), and the status type. */
export const rejectionRowMatches = (row: IRejectionRow, terms: string[]): boolean =>
    matchesFields([row.message, row.statusCode ? `Code ${row.statusCode}` : undefined, row.statusType], terms);

/** Payer control number, procedure codes, adjustment and remark codes, and the payment status. */
export const paymentRowMatches = (row: IDenialPaymentRow, terms: string[]): boolean =>
    matchesFields(
        [
            row.payerControlNumber,
            ...row.allProcedureCodes,
            ...row.adjustments.map(adjustment => adjustment.code),
            ...row.remarkCodes,
            row.status,
        ],
        terms,
    );
